import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
var __jsx = React.createElement;
import React, { useState } from "react";
import classNames from "classnames";
import { get } from "lodash";
import { useStoreState } from "pullstate";
import { useTranslation } from "react-i18next";
import { useAuth } from "@qure/qure-auth";
import { faArrowRightFromBracket, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import AwesomeIcon from "../AwesomeIcon";
import DropdownMenu from "../common/DropdownMenu";
import UserIcon from "../common/UserIcon";
import ProxyAccountStore, { clearProxyTarget, setProxyTarget } from "../../utils/proxyAccountStore";
import { getProxyAccounts } from "../../types/api/ProxyAccount";
import useProxyAccess from "./useProxyAccess";

// Show the filter box only once the list is long enough to be worth scanning.
var SEARCH_THRESHOLD = 6;
var ProxyAccountSwitcher = function ProxyAccountSwitcher() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var _useProxyAccess = useProxyAccess(),
    access = _useProxyAccess.data;
  var userEmail = get(userInfo, ["details", "email"], "");
  var proxyAccounts = getProxyAccounts(access);
  var _useState = useState(""),
    search = _useState[0],
    setSearch = _useState[1];
  var targetAccountId = useStoreState(ProxyAccountStore, function (store) {
    return store.targetAccountId;
  });
  var activeAccount = proxyAccounts.find(function (account) {
    return account.id === targetAccountId;
  }) || null;

  // Reload so every screen re-reads through the new header; some queries
  // (useWorkspaces) cache with staleTime: Infinity and never refetch otherwise.
  // Safe: localStorage is written synchronously before the reload.
  var startProxying = function startProxying(accountId) {
    setProxyTarget(accountId, userEmail);
    window.location.reload();
  };
  var stopProxying = function stopProxying() {
    clearProxyTarget();
    window.location.reload();
  };

  // No live grants, not eligible, or the feature is off - render nothing at all.
  if (!proxyAccounts.length) return null;
  var showSearch = proxyAccounts.length >= SEARCH_THRESHOLD;
  var query = search.trim().toLowerCase();
  var filteredAccounts = query ? proxyAccounts.filter(function (account) {
    return (account.name || "").toLowerCase().includes(query) || account.email.toLowerCase().includes(query);
  }) : proxyAccounts;
  var activeStatusOption = {
    name: /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-2 px-4 py-2.5 bg-amber-400 text-gray-900 rounded-t"
    }, /*#__PURE__*/React.createElement("span", {
      className: "h-1.5 w-1.5 rounded-full bg-gray-900 shrink-0"
    }), /*#__PURE__*/React.createElement("span", {
      className: "text-md font-medium uppercase tracking-wide"
    }, t("account_proxy.active_label", "Proxy active"))),
    className: "!cursor-default !bg-transparent hover:!bg-transparent",
    onClick: function onClick() {}
  };
  var sectionLabelOption = {
    name: /*#__PURE__*/React.createElement("div", {
      className: "px-4 pt-3 pb-1"
    }, /*#__PURE__*/React.createElement("span", {
      className: "text-md uppercase tracking-wide text-gray-500 dark:text-gray-400"
    }, t("account_proxy.section_label", "Proxy as"))),
    className: "!cursor-default !bg-transparent hover:!bg-transparent",
    onClick: function onClick() {}
  };
  var accountOptions = filteredAccounts.map(function (account) {
    var isActive = account.id === targetAccountId;
    var displayName = account.name || account.email;
    return {
      name: /*#__PURE__*/React.createElement("div", {
        className: "flex items-center gap-3 px-4 py-3",
        "data-testid": "proxy-account-option-".concat(account.id)
      }, /*#__PURE__*/React.createElement(UserIcon, {
        name: displayName,
        className: "h-8 w-8 text-md shrink-0"
      }), /*#__PURE__*/React.createElement("div", {
        className: "flex flex-col items-start min-w-0 flex-1"
      }, /*#__PURE__*/React.createElement("span", {
        className: "w-full truncate text-left font-medium"
      }, displayName), /*#__PURE__*/React.createElement("span", {
        className: "w-full truncate text-left text-md text-gray-400"
      }, account.email)), isActive && /*#__PURE__*/React.createElement(AwesomeIcon, {
        name: faCheck,
        size: "sm",
        className: "w-4 shrink-0 text-teal-600 dark:text-teal-400"
      })),
      className: isActive ? "bg-teal-100 dark:bg-teal-900" : "",
      onClick: function onClick() {
        return isActive ? stopProxying() : startProxying(account.id);
      }
    };
  });
  var stopOption = {
    name: /*#__PURE__*/React.createElement("div", {
      className: "mt-1 flex items-center gap-3 border-t border-gray-300 px-4 py-3 dark:border-gray-700",
      "data-testid": "proxy-account-stop"
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      name: faArrowRightFromBracket,
      className: "w-4 shrink-0"
    }), /*#__PURE__*/React.createElement("span", {
      className: "font-medium"
    }, t("account_proxy.stop", "Stop proxying"))),
    onClick: stopProxying
  };
  var noMatchOption = {
    name: /*#__PURE__*/React.createElement("div", {
      className: "px-4 py-3 text-md text-gray-500 dark:text-gray-400"
    }, t("account_proxy.no_account_match", "No account matches.")),
    className: "!cursor-default !bg-transparent hover:!bg-transparent",
    onClick: function onClick() {}
  };
  var listOptions = accountOptions.length ? accountOptions : [noMatchOption];
  var options = activeAccount ? [activeStatusOption, sectionLabelOption].concat(_toConsumableArray(listOptions), [stopOption]) : [sectionLabelOption].concat(_toConsumableArray(listOptions));
  return /*#__PURE__*/React.createElement(DropdownMenu, {
    options: options,
    hasCustomControl: true,
    isSearch: showSearch,
    search: search,
    setSearch: setSearch,
    searchPlaceholder: t("account_proxy.search_placeholder", "Search name or email"),
    scrollableContainerClassName: "max-h-[55vh]",
    menuItemClassName: "z-50 overflow-hidden",
    dataTestId: "proxy-account-switcher",
    buttonClassName: "flex items-center h-full px-3 border-b-2 border-transparent",
    Icon: function Icon(_ref) {
      var open = _ref.open;
      return /*#__PURE__*/React.createElement("span", {
        className: classNames("flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors", {
          "bg-amber-400 text-gray-900 hover:bg-amber-300": !!activeAccount,
          "bg-amber-300": !!activeAccount && open,
          "bg-gray-700 text-gray-200 hover:bg-gray-600": !activeAccount,
          "bg-gray-600": !activeAccount && open
        })
      }, !!activeAccount && /*#__PURE__*/React.createElement("span", {
        className: "h-1.5 w-1.5 shrink-0 rounded-full bg-gray-900"
      }), /*#__PURE__*/React.createElement("span", {
        className: "max-w-[10rem] truncate"
      }, activeAccount ? activeAccount.name || activeAccount.email : t("account_proxy.switcher", "Proxy account")), /*#__PURE__*/React.createElement(AwesomeIcon, {
        name: faChevronDown,
        size: "xs",
        className: classNames("shrink-0 transition-transform", {
          "rotate-180": open
        })
      }));
    }
  });
};
export default ProxyAccountSwitcher;